import { KnownOrderDtoStatus, OpenLockerResponseDto, OrderDto } from '@groundfloor-platform/mgmt-service-g-sdk';
import {
  canChangeStatus,
  getModalTitle,
  openIntentMessage,
  getUpdateStatusOptions
} from './open-locker-action.constants';
import {
  OpenLockerActionHandlers,
  OpenLockerActionProps,
  OpenLockerActionStates,
  useOpenLockerAction,
  ValidOrderStatusChangeOption
} from './open-locker-action.hook';
import { DialogActions, DialogContent } from '@mui/material';
import { Modal } from '../../../../../ui/components/pure/modal/modal.component';
import { Dropdown } from '../../../../../ui/components/pure/dropdown/dropdown.component';
import { TextButton } from '../../../../../ui/components/pure/text-button/text-button.component';
import { Alert, AlertTitle } from '@mui/material';
import { parseErrorMessage } from '../../../../../core/api/api.context';
import { OpenLockerRequestView } from '../../../../lockers/details/actions/open-locker/shared-components/open-locker-request-view.component';
import { OpenLockerNotPermitted } from '../../../../lockers/details/actions/open-locker/shared-components/open-locker-not-permitted.component';
import { useBookingFlowType } from '../../../list/actions/book-order/use-booking-flow-type.hook';
import { BookingFlowType } from '../../../../../ui/enums/booking-flow-type.enum';

export function OpenLockerModal({ order, ...rest }: OpenLockerActionProps) {
  const { state, handlers, canOpenLocker, isLoading, newStatus } = useOpenLockerAction({ order, ...rest });

  if (canOpenLocker?.canOpen === false) {
    return <OpenLockerNotPermitted onClose={handlers.onCancelIntent} />;
  }

  return (
    <OpenLockerModalComponent
      state={state}
      newStatus={newStatus}
      handlers={handlers}
      order={order}
      isLoading={isLoading}
    />
  );
}

/** OpenLockerModalComponent is only exported for use in Storybook */
export function OpenLockerModalComponent({
  order,
  handlers,
  state,
  newStatus,
  isLoading
}: {
  state: OpenLockerActionStates;
  newStatus?: ValidOrderStatusChangeOption;
  handlers: OpenLockerActionHandlers;
  order: OrderDto;
  isLoading: boolean;
}) {
  if (state.current === 'OPEN_REQUEST_INTENT_PENDING') {
    return (
      <Modal
        title={getModalTitle('OPEN_LOCKER')}
        loading={isLoading}
        open={true}
        maxWidth="xs"
        onClose={handlers.onCancelIntent}
      >
        <IntentToOpenView
          hubName={order.hub?.name}
          message={openIntentMessage[order.status as KnownOrderDtoStatus]}
          {...state}
          {...handlers}
        />
      </Modal>
    );
  }
  if (state.current === 'OPEN_REQUEST_FAILED') {
    return (
      <Modal
        title={getModalTitle('OPEN_LOCKER')}
        loading={isLoading}
        open={true}
        maxWidth="sm"
        onClose={handlers.onCancelIntent}
      >
        <OpenLockerFailedView {...handlers} {...state} />
      </Modal>
    );
  }
  if (state.current === 'OPEN_REQUEST_SUCCESS') {
    return (
      <Modal
        title={getModalTitle('CHANGE_STATUS', order.status as KnownOrderDtoStatus)}
        loading={isLoading}
        open={true}
        maxWidth="sm"
        onClose={handlers.onCancelIntent}
      >
        <StatusChangeView
          currentStatus={order.status as KnownOrderDtoStatus}
          newStatus={newStatus}
          projectId={order.retailer?.id || ''}
          {...state}
          {...handlers}
        />
      </Modal>
    );
  } else {
    return null;
  }
}

export function IntentToOpenView(
  props: { hubName?: string; message: string } & OpenLockerActionStates & OpenLockerActionHandlers
) {
  return (
    <OpenLockerRequestView
      error={props.error}
      hubName={props.hubName}
      lockerContents={{
        message: props.message
      }}
      onCancelIntent={props.onCancelIntent}
      onSendRequest={props.onSendRequest}
    />
  );
}

export function OpenLockerFailedView({
  response,
  error,
  onSendRequest,
  onContinueToStatusChange
}: { response?: OpenLockerResponseDto } & OpenLockerActionHandlers & OpenLockerActionStates) {
  let errorTitle = 'Something went wrong.';
  let errorMessage = 'There was an unexpected error.';
  let prompt = 'It looks like no doors opened. Please retry.';
  let showContinueButton = false;

  if (response) {
    if (response.failReasons.includes('Broker responded with status 500')) {
      errorTitle = 'Failed to connect to kiosk';
    }
    errorMessage = `${response.failCount} out of ${response.tryCount} door${
      response.tryCount > 1 ? 's' : ''
    } failed to open.`;

    if (response.tryCount > response.failCount) {
      prompt = 'If you were able to do what you needed, please click continue.';
      showContinueButton = true;
    }
  }

  // There could be an error after retrying so need to overwrite the response message
  if (error) {
    errorTitle = 'Something went wrong.';
    errorMessage = parseErrorMessage(error.message);
  }

  return (
    <>
      <DialogContent>
        <p>
          <Alert variant="outlined" severity="error">
            <AlertTitle>{errorTitle}</AlertTitle>
            {errorMessage}
          </Alert>
        </p>
        <p>{prompt}</p>
      </DialogContent>
      <DialogActions>
        {showContinueButton && (
          <TextButton color="secondary" variant="outlined" onClick={onContinueToStatusChange}>
            Continue
          </TextButton>
        )}
        <TextButton color="primary" variant="outlined" onClick={onSendRequest}>
          Retry
        </TextButton>
      </DialogActions>
    </>
  );
}

export function StatusChangeView(
  props: {
    currentStatus: KnownOrderDtoStatus;
    newStatus?: ValidOrderStatusChangeOption;
    projectId: string;
  } & OpenLockerActionStates &
    OpenLockerActionHandlers
) {
  const bookingFlowType = useBookingFlowType(props.projectId);
  const showStatusChange = canChangeStatus(props.currentStatus) && bookingFlowType !== BookingFlowType.INVENTORY;
  const callToActionButtonText = showStatusChange ? 'Change' : 'Done';
  const callToActionButtonHandler = showStatusChange ? props.onChangeStatus : props.onDone;
  const statusOptions = getUpdateStatusOptions(bookingFlowType);

  return (
    <>
      <DialogContent>
        <p>
          <Alert variant="outlined" severity="success">
            <p style={{ margin: 0, height: '100%', display: 'flex', alignItems: 'center', fontSize: '14px' }}>
              Locker opened
            </p>
          </Alert>
          {/* <span role="img">✔</span> Locker opened */}
        </p>
        {props.error && (
          <p>
            <Alert variant="outlined" severity="error">
              <AlertTitle>Something went wrong</AlertTitle>
              There was an error updating the status of the order
            </Alert>
          </p>
        )}
        {showStatusChange && (
          <>
            <p>Do you need to update the parcel status?</p>
            <Dropdown
              fullWidth
              shrinkLabel={false}
              dropdownVariant="primarySolid"
              textFieldDesktopWidth="60%"
              menuItems={statusOptions}
              placeholder="Select new status"
              value={props.newStatus}
              onChange={(e) => props.onStatusInput(e.target.value as ValidOrderStatusChangeOption)}
            />
          </>
        )}
      </DialogContent>
      <DialogActions style={{ borderTop: '1px solid #EBEDF2', padding: '16px 24px' }}>
        {showStatusChange && (
          <TextButton color="secondary" variant="outlined" onClick={() => props.onDone()}>
            Skip
          </TextButton>
        )}
        <TextButton color="primary" variant="outlined" onClick={() => callToActionButtonHandler()}>
          {callToActionButtonText}
        </TextButton>
      </DialogActions>
    </>
  );
}
